import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../components/Header';
import ChatAngebotRequest from '../components/ChatAngebotRequest';
import { getStats, getClubStatus, getClubInvite } from '../api/client';
import {
  Users, Briefcase, Store, Mic, MessageCircle, MapPin,
  Play, Megaphone, Video, ChevronRight, IconBadge,
} from '../components/icons';

const CLUB_FALLBACK_LINK = 'https://t.me/+rE8iCJuhEj44MDEy';
const openTg = (link) => {
  if (window.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(link);
  else window.open(link, '_blank');
};

const HP_CSS = `
.hp-root { --ease: cubic-bezier(.22,.68,0,1); }
.hp-card { transition: transform .16s var(--ease), border-color .25s ease; -webkit-tap-highlight-color: transparent; }
.hp-card:active { transform: scale(.985); }
@keyframes clubGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(255,204,0,0); } 50% { box-shadow: 0 0 22px -2px rgba(255,204,0,.42); } }
@keyframes notifIn { from { transform: translateY(calc(-100% - 24px)); } to { transform: translateY(0); } }
.hp-notif { animation: notifIn .55s cubic-bezier(.32,.72,0,1) .45s both; touch-action: none; }
@media (prefers-reduced-motion: reduce) {
  .hp-card { transition: none; }
  .club-nudge, .hp-notif { animation: none !important; }
  [class*="fadeInUp"], .hp-anim { animation: none !important; }
}
`;

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// Плейлист YouTube (youtube.com/playlist?list=… или ссылка на видео без id, но с list=)
function getYouTubePlaylistId(url) {
  if (!url) return null;
  const m = url.match(/youtube\.com\/.*[?&]list=([a-zA-Z0-9_-]{10,})/);
  return m ? m[1] : null;
}

function getLoomId(url) {
  if (!url) return null;
  const m = url.match(/loom\.com\/(?:share|embed)\/([a-f0-9]+)/);
  return m ? m[1] : null;
}

function getVideoInfo(url) {
  const ytId = getYouTubeId(url);
  if (ytId) return { type: 'youtube', id: ytId };
  const listId = getYouTubePlaylistId(url);
  if (listId) return { type: 'youtube_list', id: listId };
  const loomId = getLoomId(url);
  if (loomId) return { type: 'loom', id: loomId };
  return null;
}

function VideoEmbed({ video, title, autoplay }) {
  if (video.type === 'youtube') {
    return <iframe
      src={`https://www.youtube.com/embed/${video.id}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1`}
      title={title}
      style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />;
  }
  if (video.type === 'youtube_list') {
    return <iframe
      src={`https://www.youtube.com/embed/videoseries?list=${video.id}&autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1`}
      title={title}
      style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />;
  }
  if (video.type === 'loom') {
    return <iframe
      src={`https://www.loom.com/embed/${video.id}?autoplay=${autoplay ? 'true' : 'false'}`}
      title={title}
      style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
    />;
  }
  return null;
}

function VideoThumbnail({ video, poster }) {
  // Обложка из админки (news_video.poster) — приоритет; нужна в первую очередь для Loom,
  // у которого нет публичного превью в <img>.
  if (poster) {
    return <img src={poster} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />;
  }
  if (video.type === 'youtube') {
    return <img src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />;
  }
  if (video.type === 'loom' || video.type === 'youtube_list') {
    return <div style={{ width: '100%', aspectRatio: '16/9', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
      <Video size={40} />
    </div>;
  }
  return null;
}

// Инлайн-метка с SVG-иконкой (город, время, участники и т.п.)
const Meta = ({ icon, children, accent }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: accent ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: accent ? 600 : 400 }}>
    {React.cloneElement(icon, { size: 14 })} {children}
  </span>
);

export default function HomePage({ onNavigate, role, cardFormPending, onOpenCardForm }) {
  const [stats, setStats] = useState(null);
  const [newsVideoPlaying, setNewsVideoPlaying] = useState(false);
  const [angebotOpen, setAngebotOpen] = useState(false); // окно заявки на Chat-Angebot
  const [inClub, setInClub] = useState(null); // null=неизвестно, true/false=в группе или нет
  const [clubBusy, setClubBusy] = useState(false);
  // Закрытые пользователем уведомления «вебинар прошёл» (id событий), живут в localStorage
  const [recapDismissed, setRecapDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hp_webinar_recap_dismissed_v3') || '[]'); } catch { return []; }
  });

  const dismissRecap = (id) => {
    const next = [...recapDismissed, id];
    setRecapDismissed(next);
    try { localStorage.setItem('hp_webinar_recap_dismissed_v3', JSON.stringify(next)); } catch {}
  };

  // Свайп вверх по push-плашке закрывает её (как на айфоне); тап — переход в Библиотеку.
  // ВАЖНО: у плашки есть CSS-анимация появления (fill: both) — пока она «держит» элемент,
  // инлайновый transform игнорируется, поэтому по окончании анимации снимаем её (onAnimationEnd).
  const notifRef = useRef(null);
  const notifTouch = useRef({ startY: null, dy: 0, moved: false });

  const notifTouchStart = (e) => {
    notifTouch.current = { startY: e.touches[0].clientY, dy: 0, moved: false };
    const el = notifRef.current;
    if (el) { el.style.animation = 'none'; el.style.transition = 'none'; }
  };
  const notifTouchMove = (e) => {
    const t = notifTouch.current;
    if (t.startY == null || !notifRef.current) return;
    // вверх — свободно, вниз — с «резинкой», как у системных уведомлений
    const raw = e.touches[0].clientY - t.startY;
    if (Math.abs(raw) > 8) t.moved = true;
    t.dy = raw < 0 ? raw : Math.min(raw * 0.15, 12);
    notifRef.current.style.transform = `translateY(${t.dy}px)`;
  };
  const notifTouchEnd = (id) => {
    const el = notifRef.current;
    const { dy } = notifTouch.current;
    notifTouch.current.startY = null;
    if (!el) return;
    if (dy < -25) {
      el.style.transition = 'transform .25s ease, opacity .25s ease';
      el.style.transform = 'translateY(calc(-100% - 34px))';
      el.style.opacity = '0';
      setTimeout(() => dismissRecap(id), 240);
    } else {
      el.style.transition = 'transform .3s cubic-bezier(.32,.72,0,1)';
      el.style.transform = 'translateY(0)';
    }
  };
  // Свайп не должен срабатывать как тап (click в вебвью может прилететь и после драга)
  const notifClick = (id) => {
    if (notifTouch.current.moved) return;
    dismissRecap(id);
    onNavigate('library');
  };

  const load = useCallback(() => {
    getStats().then(setStats).catch(console.error);
  }, []);

  const loadClub = useCallback(() => {
    getClubStatus().then(d => setInClub(d?.in_club ?? null)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadClub(); }, [load, loadClub]);

  useEffect(() => {
    const onVisible = () => { if (!document.hidden) { load(); loadClub(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load, loadClub]);

  // Вступление в чат клуба: берём свежую персональную ссылку, открываем группу,
  // затем перепроверяем членство (юзер мог только что вступить → нудж исчезнет).
  const joinClub = useCallback(async () => {
    if (clubBusy) return;
    setClubBusy(true);
    let link = CLUB_FALLBACK_LINK;
    try { const d = await getClubInvite(); if (d?.invite_link) link = d.invite_link; } catch {}
    openTg(link);
    setClubBusy(false);
    setTimeout(loadClub, 4000);
  }, [clubBusy, loadClub]);

  const s = stats || {};

  // Единая строка-карточка со SVG-бейджем (чат / партнёрка / темы эксперта)
  const STAT_CARDS = [
    { Icon: Users, num: s.partners || s.members || '—', label: 'Участников', url: s.partners_url || 'https://fix-hub.de/handwerker' },
    { Icon: Briefcase, num: s.experts || '—', label: 'Экспертов', url: s.experts_url || 'https://fix-hub.de/experten' },
    { Icon: Store, num: s.shops || '—', label: 'Магазинов', url: s.shops_url || 'https://shop.fix-hub.de/' },
  ];

  return (
    <div className="hp-root">
      <style>{HP_CSS}</style>

      {/* Push-плашка «как в айфоне»: вебинар прошёл → запись в Библиотеке.
          Выезжает сверху, свайп вверх закрывает, тап открывает Библиотеку. */}
      {s.recent_webinar && !recapDismissed.includes(s.recent_webinar.id) && (
        <div ref={notifRef} className="hp-notif"
          onAnimationEnd={(e) => { e.currentTarget.style.animation = 'none'; }}
          onTouchStart={notifTouchStart}
          onTouchMove={notifTouchMove}
          onTouchEnd={() => notifTouchEnd(s.recent_webinar.id)}
          onTouchCancel={() => notifTouchEnd(s.recent_webinar.id)}
          style={{
            position: 'fixed',
            top: 'calc(var(--tg-safe-top, env(safe-area-inset-top, 0px)) + 12px)',
            left: 10, right: 10, zIndex: 1000,
          }}>
          <div
            onClick={() => notifClick(s.recent_webinar.id)}
            style={{
              background: 'rgba(44,44,48,0.88)',
              backdropFilter: 'blur(24px) saturate(1.6)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
              borderRadius: 20, padding: '11px 13px',
              border: '1px solid rgba(255,255,255,0.10)',
              boxShadow: '0 10px 34px rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', gap: 11,
              cursor: 'pointer', color: '#fff',
            }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="" style={{ width: 30, height: 30, objectFit: 'contain' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Вебинар прошёл</span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', flexShrink: 0 }}>сейчас</span>
              </div>
              <div style={{
                fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 1, lineHeight: 1.35,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {s.recent_webinar.title} — запись уже в Библиотеке, нажмите чтобы посмотреть
              </div>
            </div>
          </div>
          {/* Полоска-грабер как у системной шторки — намекает на свайп */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 5 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.28)' }} />
          </div>
        </div>
      )}

      <Header
        title={<img src="/logo.png" alt="Fix-Hub" style={{
          height: 32, width: 'auto', maxWidth: '62vw', objectFit: 'contain', display: 'block',
        }} />}
      />
      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Плашка «анкета карточки fix-hub.de не заполнена» — оплатившим standard/pro
            без отправленной анкеты (флаг card_form_pending из /api/subscription).
            Тап открывает анкету (App.js → PostPaymentDev). */}
        {cardFormPending && (
          <div
            onClick={() => onOpenCardForm && onOpenCardForm()}
            className="hp-card"
            style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: 14,
              border: '1px solid var(--accent)', cursor: 'pointer',
              animation: 'fadeInUp 0.4s ease both, clubGlow 2.6s ease 0.6s infinite',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                background: 'var(--accent-dim)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Briefcase size={22} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Карточка вашей фирмы не опубликована</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.35 }}>
                  Вы не заполнили анкету для карточки на платформе fix-hub.de — это 5 минут
                </div>
              </div>
              <span style={{ color: 'var(--accent)', display: 'flex', flexShrink: 0 }}><ChevronRight size={18} /></span>
            </div>
          </div>
        )}

        {/* Dashboard Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, animation: 'fadeInUp 0.4s ease both' }}>
          {STAT_CARDS.map(({ Icon, num, label, url }, i) => (
            <div key={i}
              onClick={() => window.Telegram?.WebApp?.openLink ? window.Telegram.WebApp.openLink(url) : window.open(url, '_blank')}
              className="hp-card"
              style={{
                background: 'var(--bg-card)', borderRadius: 14, padding: '16px 10px',
                border: '1px solid var(--border)', textAlign: 'center', cursor: 'pointer',
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: 11, margin: '0 auto 9px',
                background: 'var(--accent-dim)', color: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Icon size={21} /></div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{num}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* LIVE Event — always on top when active; с прямой ссылкой открывает эфир в один тап */}
        {s.live_event && (
          <div
            onClick={() => {
              const link = s.live_event.link;
              if (link) {
                try {
                  if (window.Telegram?.WebApp?.openLink) window.Telegram.WebApp.openLink(link);
                  else window.open(link, '_blank');
                } catch { window.open(link, '_blank'); }
              } else onNavigate('events');
            }}
            className="hp-card"
            style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: 16,
              border: '1px solid rgba(255,69,58,0.4)', cursor: 'pointer',
              animation: 'fadeInUp 0.4s ease 0.05s both',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{
                fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                background: '#FF453A', color: '#fff',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'pulseDot 1.2s ease infinite' }}></span>
                LIVE
              </span>
              <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={18} /></span>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{s.live_event.title}</div>
            <div style={{ display: 'flex', gap: 14, fontSize: 13 }}>
              {s.live_event.speaker && <Meta icon={<Mic />}>{s.live_event.speaker}</Meta>}
              <Meta icon={<Users />}>{s.live_event.registrations_count || 0} участников</Meta>
            </div>
            {s.live_event.link && (
              <div style={{
                marginTop: 12, background: '#FF453A', color: '#fff',
                borderRadius: 12, padding: '12px 16px', textAlign: 'center',
                fontSize: 15, fontWeight: 700,
              }}>▶️ Подключиться к эфиру</div>
            )}
          </div>
        )}

        {/* Chat Button — адаптивный: не в клубе → акцентный нудж «Войти», в клубе → спокойный вид */}
        {inClub === false ? (
          <div className="club-nudge hp-card" onClick={joinClub}
            style={{
              background: 'linear-gradient(135deg, rgba(255,204,0,0.12), var(--bg-card))',
              borderRadius: 16, padding: '16px', cursor: 'pointer',
              border: '1px solid rgba(255,204,0,0.5)',
              display: 'flex', alignItems: 'center', gap: 14,
              animation: 'fadeInUp 0.4s ease 0.08s both, clubGlow 2.6s ease-in-out infinite',
            }}>
            <IconBadge accent size={44} iconSize={22}><MessageCircle /></IconBadge>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Вы ещё не в чате клуба</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Всё общение, вопросы и ответы — там</div>
            </div>
            <span style={{
              flexShrink: 0, background: 'var(--accent)', color: '#000',
              fontSize: 14, fontWeight: 700, padding: '9px 14px', borderRadius: 12,
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>{clubBusy ? '…' : <>Войти <ChevronRight size={15} /></>}</span>
          </div>
        ) : (
          <div onClick={() => openTg(CLUB_FALLBACK_LINK)} className="hp-card"
            style={{
              background: 'linear-gradient(135deg, rgba(10,132,255,0.10), rgba(10,132,255,0.02))',
              borderRadius: 16, padding: '18px 16px', cursor: 'pointer',
              border: '1px solid rgba(10,132,255,0.35)',
              display: 'flex', alignItems: 'center', gap: 14,
              animation: 'fadeInUp 0.4s ease 0.08s both',
            }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14, flexShrink: 0,
              background: 'rgba(10,132,255,0.15)', color: '#0A84FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><MessageCircle size={26} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Чат клуба</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>Общение, вопросы, нетворкинг</div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              padding: '6px 10px', borderRadius: 20, background: 'rgba(48,209,88,0.12)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', animation: 'pulseDot 1.4s ease infinite' }}></span>
              <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 700 }}>online</span>
            </div>
          </div>
        )}

        {/* Latest Object */}
        {s.latest_object && (
          <div onClick={() => onNavigate('objects')} className="hp-card"
            style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: 16,
              border: '1px solid var(--border)', cursor: 'pointer',
              animation: 'fadeInUp 0.4s ease 0.1s both',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'var(--accent-dim)', color: 'var(--accent)' }}>Новый объект</span>
              <span style={{ color: 'var(--text-tertiary)', display: 'flex' }}><ChevronRight size={18} /></span>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{s.latest_object.title}</div>
            <div style={{ display: 'flex', gap: 14, fontSize: 13, alignItems: 'center' }}>
              <Meta icon={<MapPin />}>{s.latest_object.city}</Meta>
              {s.latest_object.budget > 0 ? (
                <Meta icon={<span style={{ fontWeight: 700, fontSize: 13, lineHeight: 1 }}>€</span>} accent>{s.latest_object.budget.toLocaleString()}</Meta>
              ) : s.latest_object.budget_label ? (
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.latest_object.budget_label}</span>
              ) : null}
            </div>
          </div>
        )}

        {/* Next Event (only if no live event, or different from live) */}
        {s.next_event && (!s.live_event || s.next_event.id !== s.live_event.id) && (
          <div onClick={() => onNavigate('events')} className="hp-card"
            style={{
              background: 'linear-gradient(135deg, rgba(255,204,0,0.12), rgba(255,204,0,0.03))',
              borderRadius: 16, padding: 16,
              border: '1px solid rgba(255,204,0,0.4)',
              cursor: 'pointer',
              animation: 'fadeInUp 0.4s ease 0.12s both',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{
                fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 6,
                background: '#FFCC00', color: '#0A0A0A', letterSpacing: 0.5, textTransform: 'uppercase',
              }}>{({ webinar: 'Вебинар', seminar: 'Семинар', meetup: 'Встреча' })[s.next_event.type] || 'Событие'}</span>
              {/* «Скоро»: сколько дней до события (сегодня/завтра/через N дн.) — чтобы анонс светился, а не просто висел */}
              {s.next_event.date && (() => {
                const days = Math.ceil((new Date(s.next_event.date) - Date.now()) / 86400000);
                const label = days <= 0 ? 'Сегодня' : days === 1 ? 'Завтра' : `Через ${days} дн.`;
                return (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, marginLeft: 6,
                    background: 'rgba(255,204,0,0.15)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)', animation: 'pulseDot 1.6s ease-in-out infinite' }} />
                    Скоро · {label}
                  </span>
                );
              })()}
              <span style={{ color: 'var(--text-tertiary)', display: 'flex', marginLeft: 'auto' }}><ChevronRight size={18} /></span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8, lineHeight: 1.3 }}>
              {s.next_event.title}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
              {s.next_event.date && (
                <span style={{ color: 'var(--accent)', fontWeight: 700 }}>
                  {new Date(s.next_event.date).toLocaleDateString('ru', { day: 'numeric', month: 'long' })}, {new Date(s.next_event.date).toLocaleTimeString('de', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <span>{(s.next_event.location || '').split(',')[0].trim() || 'Online'}</span>
              {s.next_event.registrations_count > 0 && <Meta icon={<Users />}>{s.next_event.registrations_count}</Meta>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.4 }}>
              {(s.next_event.type === 'webinar' || s.next_event.type === 'seminar')
                ? 'Подробности и подключение → регистрация не нужна'
                : 'Программа и регистрация →'}
            </div>
          </div>
        )}

        {/* News / Update Video */}
        {s.news_video && getVideoInfo(s.news_video.url) && (
          <div style={{
            background: 'var(--bg-card)', borderRadius: 16, overflow: 'hidden',
            border: '1px solid rgba(255,59,48,0.3)',
            animation: 'fadeInUp 0.4s ease 0.15s both',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 12, left: 12, zIndex: 2,
              background: '#FF453A', color: '#fff',
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              padding: '4px 10px', borderRadius: 6,
              boxShadow: '0 2px 8px rgba(255,69,58,0.4)',
            }}>НОВОЕ</div>
            {!newsVideoPlaying ? (
              <div onClick={() => setNewsVideoPlaying(true)} style={{ position: 'relative', cursor: 'pointer' }}>
                <VideoThumbnail video={getVideoInfo(s.news_video.url)} poster={s.news_video.poster} />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'rgba(255,69,58,0.95)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(255,69,58,0.4)', paddingLeft: 4,
                  }}>
                    <Play size={26} />
                  </div>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#FF453A', display: 'flex' }}><Megaphone size={18} /></span>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{s.news_video.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Нажмите, чтобы посмотреть</div>
                </div>
              </div>
            ) : (
              <div>
                <VideoEmbed video={getVideoInfo(s.news_video.url)} title={s.news_video.title} autoplay />
                <div style={{ padding: '10px 16px' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{s.news_video.title}</span>
                </div>
              </div>
            )}
            {s.news_video.button && (
              <div style={{ padding: '2px 16px 14px' }}>
                <button
                  className="hp-card"
                  onClick={() => {
                    // Ссылка на chat-angebot → внутреннее окно заявки; любая другая — внешняя, как раньше.
                    if (/chat-angebot/i.test(s.news_video.button.url)) { setAngebotOpen(true); return; }
                    if (window.Telegram?.WebApp?.openLink) window.Telegram.WebApp.openLink(s.news_video.button.url);
                    else window.open(s.news_video.button.url, '_blank');
                  }}
                  style={{
                    width: '100%', padding: '13px 16px', borderRadius: 12, border: 'none',
                    background: 'var(--accent)', color: '#0D0D0D', fontSize: 15, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  {s.news_video.button.text} <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {angebotOpen && <ChatAngebotRequest onClose={() => setAngebotOpen(false)} />}
    </div>
  );
}
